import { Component } from '@angular/core';
import { StepperQueries } from '../../../../core/store/stepper/stepper.queries';
import { EntityQueries } from '../../../../core/store/entity/entity.queries';
import { merge, Observable } from 'rxjs';
import { HttpService } from '../../../../core/services/http/http.service';
import { ConfigQueries } from '../../../../core/store/config/config.queries';
import { BasePageComponent } from '../../../../core/components/component-base-page/base-page.component';
import { OnboardingQueries } from '../../../../core/store/onboarding/onboarding.queries';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OnboardingStateModel } from '../../../../core/store/onboarding/onboarding.state';
import { takeUntil } from 'rxjs/operators';
import { ConfigQuestion } from '../../../../core/models/config-question.interface';

@Component({
	selector: 'bcb-entity-registered-address',
	templateUrl: './entity-registered-address.component.html',
	styleUrls: ['./entity-registered-address.component.scss'],
})
export class EntityRegisteredAddressComponent extends BasePageComponent {
	componentLayoutClassList: string[] = ['col-12'];
	private operatingAddressWatchStarted = false;

	constructor(
		configQueries: ConfigQueries,
		entityQueries: EntityQueries,
		http: HttpService,
		onboardingQueries: OnboardingQueries,
		stepperQueries: StepperQueries,
		_snackBar: MatSnackBar
	) {
		super(
			configQueries,
			entityQueries,
			http,
			onboardingQueries,
			stepperQueries,
			_snackBar
		);
	}

	get registeredAddressQuestions(): ConfigQuestion[] {
		const index = this.operatingHeadingIndex();
		return index < 0 ? this.questions : this.questions.slice(0, index);
	}

	get operatingAddressQuestions(): ConfigQuestion[] {
		const index = this.operatingHeadingIndex();
		return index < 0 ? [] : this.questions.slice(index + 1);
	}

	handleOnboardingResponse(data: OnboardingStateModel): void {
		super.handleOnboardingResponse(data);
		this.keepOperatingAddressInSync(true);
		this.watchRegisteredAddress();
	}

	formatOnboardingAnswers(): { justification: string; [key: string]: any } {
		const answers = super.formatOnboardingAnswers();
		const sameAsRegistered =
			this.entityUtils.questionKeys.same_as_registered;
		if (!this.formGroup.get(sameAsRegistered)?.value) {
			return answers;
		}

		const raw = this.formGroup.getRawValue();
		for (const key of this.entityUtils.operatingAddressKeys()) {
			if (this.visibleControls?.has(key)) {
				answers[key] = raw[key];
			}
		}
		return answers;
	}

	validateAndSave(): void {
		this.entityUtils.copyOperatingAddressFromRegistered(this.formGroup);
		super.validateAndSave();
	}

	private operatingHeadingIndex(): number {
		const headingKey = this.entityUtils.questionKeys.operating_heading;
		return (
			this.questions?.findIndex(
				(question) => question.key === headingKey
			) ?? -1
		);
	}

	private keepOperatingAddressInSync(preservePristine: boolean): void {
		const pristine = this.formGroup.pristine;
		const changed = this.entityUtils.syncOperatingAddressWithRegistered(
			this.formGroup,
			this.isDisabled
		);
		if (preservePristine && pristine && !changed) {
			this.formGroup.markAsPristine();
		}
	}

	private watchRegisteredAddress(): void {
		if (this.operatingAddressWatchStarted) {
			return;
		}
		this.operatingAddressWatchStarted = true;

		const keys = this.entityUtils.questionKeys;
		const watched = [
			keys.same_as_registered,
			keys.street,
			keys.sub_street,
			keys.city,
			keys.state,
			keys.us_state,
			keys.postcode,
			keys.country,
		]
			.map((key) => this.formGroup.get(key)?.valueChanges)
			.filter((changes): changes is Observable<unknown> => !!changes);

		merge(...watched)
			.pipe(takeUntil(this.questionsConfig.unsubscribe$))
			.subscribe(() => this.keepOperatingAddressInSync(false));
	}

	onStepComplete(): Observable<any> {
		return this.http.updateAddress({
			...this.entityUtils.mapQuestionsToEntity(this.formGroup.value)
				?.address,
			entity_id: this.entity?.id,
		});
	}
}
